import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon, XCircleIcon, CheckCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Link } from 'wouter';
import moment from 'moment';
import Swal from 'sweetalert2';
import { updateDocVisibility, v2GetEmployeeDocuments } from '../services/api';
import  { getSignedDocs } from '../services/api_firme';
import NoPermissions from '../authorization/NoPermissions';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { deleteDoc } from '../services/api';
import CustomTooltipComponent from './custom_tooltip_component';
import FilePreviewModal from './preview_modal';
import EditDocumentModal from './edit_document_modal';
import { buildDefaultEmailCsv, formatEmails } from '../services/utils_email';
import { getDocumentRecipientsDefault } from '../services/api';
import FirmeDocsTable from './firme_docs_table';




const clampTiming = (v) => {
  if (v == null) return null;
  const n = parseInt(String(v), 10);
  if (Number.isNaN(n)) return null;
  if (n < 1) return 1;
  if (n > 31) return 31;
  return n;
};

const getEffectiveDocNotifications = (doc, defaults) => {
  const defaultsEnabled = defaults?.default_days != null;

  const timing =
    doc?.notification_timing != null
      ? clampTiming(doc.notification_timing)
      : (defaultsEnabled ? clampTiming(defaults.default_days) : null);

  let email = "";
  if (doc?.notification_email != null && String(doc.notification_email).trim() !== "") {
    email = String(doc.notification_email).trim();
  } else if (defaultsEnabled && timing != null) {   // <-- aggiunto timing != null
    email = buildDefaultEmailCsv({
      send_company: !!defaults?.send_company,
      company_email: String(defaults?.company_email || "").trim(),
      send_handlers: !!defaults?.send_handlers,
      handlers_emails: Array.isArray(defaults?.handlers_emails) ? defaults.handlers_emails : [],
      others_emails: String(defaults?.others_emails || ""),
    }).trim();
  }

  return { timing, email };
};


const DocumentiContent = ({ employee, profile, token, onUpdateDocs, selectedTab, onSelectedTabChange, }) => {
    const [previewDoc, setPreviewDoc] = useState(null);
    const [openDocId, setOpenDocId] = useState(null);
    const [openSignedDocId, setOpenSignedDocId] = useState(null);  
    const closePreview = () => setPreviewDoc(null);
    const [docs, setDocs] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(true);
    const [errorDocs, setErrorDocs] = useState(null); 
    const [editDoc, setEditDoc] = useState(null);
    const [editDocType, setEditDocType] = useState("document");
    const [signedDocs, setSignedDocs] = useState([]);
    const [loadingSignedDocs, setLoadingSignedDocs] = useState(false);
    const [errorSignedDocs, setErrorSignedDocs] = useState(null);

    const employeeId = employee?.id ?? employee?.employee?.id ?? null;

    const canEditDocument = employee?.app_activate && employee?.has_active_contract;
    
    const [docDefaults, setDocDefaults] = useState({
        loading: false,
        loaded: false,
        default_days: null,
        send_company: false,
        send_handlers: false,
        company_email: "",
        company_label: "Azienda",
        handlers_emails: [],
        others_emails: "",
    });

    useEffect(() => {
    if (!token || !employeeId) return;

    let cancelled = false;

    (async () => {
        try {
        setDocDefaults((p) => ({ ...p, loading: true }));

        const data = await getDocumentRecipientsDefault(token, employeeId);
        if (cancelled) return;

        setDocDefaults({
            loading: false,
            loaded: true,
            default_days: data?.default_days ?? null,
            send_company: !!data?.send_company,
            send_handlers: !!data?.send_handlers,
            company_email: String(data?.company_email || "").trim().toLowerCase(),
            company_label: String(data?.company_label || "Azienda").trim(),
            handlers_emails: Array.isArray(data?.handlers_emails) ? data.handlers_emails : [],
            others_emails: String(data?.others_emails || ""),
        });
        } catch {
        if (cancelled) return;
        setDocDefaults((p) => ({ ...p, loading: false, loaded: true, default_days: null }));
        }
    })();

    return () => { cancelled = true; };
    }, [token, employeeId]);

    
    const hasPermission =
        profile.customer_type === 'employee' &&
        (
            profile?.employee?.permissions.includes('hr.view_docs_ou') ||
            profile?.employee?.permissions.includes('hr.view_docs_all')
        );
    
    if (profile.customer_type === 'employee' && !hasPermission) {
        return <NoPermissions />;
    }

    useEffect(() => {
        if (!employee?.cf || !token) return;

        let aborted = false;
        const controller = new AbortController();

        async function fetchDocs() {
            try {
                setLoadingDocs(true);
                setErrorDocs(null);

                const resp = await v2GetEmployeeDocuments(
                    token,
                    employee.cf,
                    controller.signal,
                    'documents'
                );

                if (!aborted) {
                    setDocs(resp || []);
                }
            } catch (err) {
                if (!aborted) {
                    console.error("Errore caricamento documenti:", err);
                    setErrorDocs("Impossibile caricare i documenti");
                    setDocs([]);
                }
            } finally {
                if (!aborted) {
                    setLoadingDocs(false);
                }
            }
        }

        fetchDocs();

        return () => {
            aborted = true;
            controller.abort();
        };
    }, [employee?.cf, token]);

    const uploadedDocs = docs?.filter((doc) => !doc.is_signed);
    const normalizeForEdit = (d) => ({
        ...d,

        id: d.id,
        name: d.name ?? d.nome ?? "",
        description: d.description ?? d.descrizione ?? "",

        due_date: d.due_date ?? null,

        notification_email: d.notification_email ?? "",
        notification_timing:
            d.notification_timing === null || d.notification_timing === undefined
            ? null
            : d.notification_timing,

        download_link: d.download_link ?? d.file ?? null,
        file: d.file ?? d.download_link ?? null,

        employee: employee?.employee ?? {
            id: employee?.id,
            cf: employee?.cf,
            first_name: employee?.first_name,
            last_name: employee?.last_name,
        },
    });

    const fetchSignedDocs = useCallback(async () => {
        if (!token || !employeeId) return;

        setLoadingSignedDocs(true);
        setErrorSignedDocs(null);

        try {
            const response = await getSignedDocs(
                token,
                { employeeId }
            );

            setSignedDocs(response || []);
        } catch (err) {
            console.error(err);
            setErrorSignedDocs(
                "Impossibile caricare i documenti con firma"
            );
        } finally {
            setLoadingSignedDocs(false);
        }
    }, [token, employeeId]);

    useEffect(() => {
        if (selectedTab === "firmati") {
            fetchSignedDocs();
        }
    }, [selectedTab, fetchSignedDocs]);

    const handleVisibilityChange = (docId, currentVisibility) => {
        const message = currentVisibility
            ? 'Vuoi rendere il documento non visibile al dipendente?'
            : 'Vuoi rendere il documento visibile al dipendente?';

        Swal.fire({
            title: message,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sì, confermo',
            cancelButtonText: 'No, annulla',
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Aggiornamento in corso...', 
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => Swal.showLoading(),
                });

                updateDocVisibility(token, docId, !currentVisibility)
                .then(() => {
                    const newDocs = docs.map(d =>
                        d.id === docId ? { ...d, visible_to_employee: !currentVisibility } : d
                    );
                    setDocs(newDocs);

                    if (onUpdateDocs) {
                        onUpdateDocs(newDocs);
                    }

                    Swal.fire('Salvato!', '', 'success');
                })
                .catch(() => {
                    Swal.fire('Errore!', 'Errore durante l\'aggiornamento.', 'error');
                });
            }
        });
    };


    const handleDeleteDoc = async (docId) => {
        const { isConfirmed } = await Swal.fire({
            title: 'Confermi eliminazione?',
            text : 'L’operazione non è reversibile e il file non potrà essere recuperato.',
            icon : 'warning',
            showCancelButton : true,
            confirmButtonText : 'Sì, elimina',
            cancelButtonText  : 'Annulla',
        });
        if (!isConfirmed) return;

        try {
            Swal.fire({
                title: 'Eliminazione in corso…',
                allowOutsideClick: false,
                allowEscapeKey  : false,
                didOpen: () => Swal.showLoading(),
            });

            await deleteDoc(token, docId);

            const newDocs = docs.filter(d => d.id !== docId);
            setDocs(newDocs);

            if (onUpdateDocs) {
                onUpdateDocs(newDocs);
            }

            Swal.fire({
                icon : 'success',
                title: 'Cancellato con successo',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (error) {
            let msg = 'Errore, riprova più tardi.';
            if (error?.response?.data) {
                const apiMsg = Object.values(error.response.data).flat();
                if (apiMsg.length) msg = apiMsg.join(' ');
            }

            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text : msg,
            });
        }
    };

    const handleEditSaved = (updated) => {
        const newDocs = docs.map((d) => {
            if (d.id !== updated.id) return d;

            return {
                ...d,
                ...updated,
                nome: (('name' in updated) ? updated.name : (d.nome ?? d.name)) ?? '',
                descrizione: (('description' in updated) ? updated.description : (d.descrizione ?? d.description)) ?? '',
                due_date: ('due_date' in updated) ? updated.due_date : d.due_date,
                notification_email: ('notification_email' in updated) ? updated.notification_email : d.notification_email,
                notification_timing: ('notification_timing' in updated) ? updated.notification_timing : d.notification_timing,
                visible_to_employee: ('visible_to_employee' in updated) ? updated.visible_to_employee : d.visible_to_employee,
                download_link: ('download_link' in updated) ? updated.download_link : d.download_link,
            };
        });
        setDocs(newDocs);
        onUpdateDocs?.(newDocs);

        Swal.fire({
            icon: 'success',
            title: 'Documento aggiornato',
            timer: 1500,
            showConfirmButton: false,
        });

        setEditDoc(null);
    };

    const handleSignedDocEditSaved = (updated) => {
        setSignedDocs((prev) =>
            prev.map((doc) =>
                Number(doc.id) === Number(updated.id)
                    ? { ...doc, ...updated }
                    : doc
            )
        );
        setEditDoc(null);
    };

    return (
        <div>
            <div className="flex justify-between items-center gap-4">
                <div className="inline-flex rounded-full bg-gray-100 p-1 shadow-sm">
                    <button
                        className={`flex items-center gap-1 px-6 py-2 text-sm font-semibold rounded-full transition-colors duration-200 min-w-[120px] ${
                        selectedTab === 'caricati'
                            ? 'bg-white shadow text-indigo-600 uppercase'
                            : 'text-gray-500 hover:text-indigo-600'
                        }`}
                        onClick={() => onSelectedTabChange('caricati')}
                    >
                        <UploadFileOutlinedIcon fontSize="small" />
                        Caricati
                    </button>

                    <button
                        className={`flex items-center gap-1 px-6 py-2 text-sm font-semibold rounded-full transition-colors duration-200 min-w-[120px] ${
                        selectedTab === 'firmati'
                            ? 'bg-white shadow text-indigo-600 uppercase'
                            : 'text-gray-500 hover:text-indigo-600'
                        }`}
                        onClick={() => onSelectedTabChange('firmati')}
                    >
                        <DrawOutlinedIcon fontSize="small" />
                        Con Firma
                    </button>
                </div>

                {/* Pulsante “Carica nuovo” disponibile per entrambe le tab  */}
                <Link
                    to={`/dipendenti/${employee.cf}/${selectedTab === 'caricati' ? 'upload_documento' : 'upload_documento_firmato'}`}
                    className="relative w-32 inline-flex items-center justify-center gap-1 rounded-sm bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                {selectedTab === 'caricati'
                    ? <UploadFileOutlinedIcon fontSize="small" />
                    : <DrawOutlinedIcon fontSize="small" />}
                    Carica Nuovo
                </Link>
            </div>

            {selectedTab === 'firmati' ? (
                <div className="mt-6 min-w-0 w-full">
                    {loadingSignedDocs ? (
                        <div className="py-8 text-center text-sm text-gray-500">
                            Caricamento documenti con firma...
                        </div>
                    ) : errorSignedDocs ? (
                        <div className="py-8 text-center text-sm text-red-600">
                            {errorSignedDocs}
                        </div>
                    ) : (
                        <FirmeDocsTable
                            docs={signedDocs}
                            employeeDetail={true}
                            employeeId={employeeId}
                            employeeCf={employee?.cf}
                            onEditDeadline={(selectedDoc) => {
                                setEditDocType("signed_document");
                                setEditDoc({
                                    ...selectedDoc,
                                    employee: selectedDoc.employee,
                                });
                            }}
                            onChanged={fetchSignedDocs}
                        />
                    )}
                </div>
            ) : (
                <div className='overflow-x-auto'>
                    <table className="min-w-full mt-8 divide-y divide-gray-200">
                        <colgroup>
                            <col className="w-[5%]" />
                            <col className="w-[35%]" />
                            <col className="w-[25%]" />
                            <col className="w-[25%]" />
                            <col className="w-[10%]" />
                        </colgroup>
                        <thead className="bg-white">
                            <tr className="text-left text-sm font-semibold text-gray-900">
                                <th className="pb-3"></th>
                                <th className="pb-3 pl-4">Nome</th>
                                <th className="pb-3 pl-4">Aperto</th>
                                <th className="pb-3 pt-2 text-center">Visibile al dipendente</th>
                                <th className="pb-3 text-center"></th>
                            </tr>
                        </thead>

                        <tbody className="bg-white divide-y divide-gray-200">
                            {loadingDocs ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center text-sm text-gray-500">
                                        Caricamento documenti...
                                    </td>
                                </tr>
                            ) : errorDocs ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center text-sm text-red-600">
                                        {errorDocs}
                                    </td>
                                </tr>
                            ) : uploadedDocs.length ? (
                                uploadedDocs.map((d) => (
                                    <EmployeeDocumentRow
                                        key={d.id}
                                        doc={d}
                                        docDefaults={docDefaults}
                                        openDocId={openDocId}
                                        setOpenDocId={setOpenDocId}
                                        setPreviewDoc={setPreviewDoc}
                                        onToggleVisible={handleVisibilityChange}
                                        onDelete={handleDeleteDoc}
                                        onEdit={(docToEdit) => {
                                            setEditDocType("document");
                                            setEditDoc(normalizeForEdit(docToEdit));
                                        }}
                                        canEditDocument={canEditDocument}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center text-sm text-gray-500 italic">
                                        Nessun documento trovato
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <FilePreviewModal
                doc={previewDoc}
                onClose={() => setPreviewDoc(null)}
            />

            {editDoc && (
                <EditDocumentModal
                    open={true}
                    onClose={() => setEditDoc(null)}
                    doc={editDoc}
                    documentType={editDocType}
                    mode="edit"
                    onSaved={
                        editDocType === "signed_document"
                            ? (updated) => {
                                handleSignedDocEditSaved(updated);
                                fetchSignedDocs();
                            }
                            : handleEditSaved
                    }
                    showPreviewModal={false}
                />
            )}
        </div>
    );
};

export default DocumentiContent;




function EmployeeDocumentRow({ doc, docDefaults, openDocId, setOpenDocId, onToggleVisible, onDelete, setPreviewDoc, onEdit, canEditDocument }) {
    const open   = openDocId === doc.id;
    const toggle = () => setOpenDocId(open ? null : doc.id);

    const { timing: effectiveTiming, email: effectiveEmail } = useMemo(
        () => getEffectiveDocNotifications(doc, docDefaults),
        [doc, docDefaults]
    );

    return (
        <>
            {/* === RIGA HEAD === */}
            <tr
                className="hover:bg-gray-50 cursor-pointer"
                onClick={toggle}
            >
                {/* freccia */}
                <td className="w-8 px-2 align-middle">
                    {open ? (
                        <ChevronUpIcon  className="w-5 h-5 text-gray-600" />
                    ) : (
                        <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                    )}
                </td>

                {/* dati principali */}
                <td className="py-2 pl-4 text-sm font-medium text-gray-900 align-middle truncate whitespace-normal">
                    {doc.nome}
                </td>

                {/* stato lettura */}
                <td className="py-2 pl-4 text-sm font-medium text-gray-900 align-middle truncate whitespace-nowrap">
                    {doc?.status === 'read' ? (
                        <>
                            <CheckCircleIcon className="h-4 w-4 text-green-500 inline-block mr-1" />
                            {doc.read_at ? moment(doc.read_at).format("DD/MM/YYYY HH:mm") : ''}
                        </>
                    ) : (
                        <>
                            <XCircleIcon className="h-4 w-4 text-red-500 inline-block mr-1" />
                            <span className="text-red-500">Non letto</span>
                        </>
                    )}
                </td>

                {/* visibilità */}
                <td className="py-2 text-sm align-middle">
                    <div className="flex justify-center">
                        <CustomTooltipComponent title='Modifica visibilità' placement='left'>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleVisible(doc.id, doc.visible_to_employee);
                                }}
                                className={`p-2 rounded ${
                                doc.visible_to_employee
                                    ? 'hover:text-red-700'
                                    : 'hover:text-green-700'
                                } text-white`}
                            >
                                {doc.visible_to_employee ? (
                                    <EyeIcon className="h-5 w-5 text-green-600" />
                                ) : (
                                    <EyeSlashIcon className="h-5 w-5 text-red-600" />
                                )}
                            </button>
                        </CustomTooltipComponent>
                    </div>
                </td>

                {/* azioni */}
                <td className="py-2 align-middle">
                    <div className="flex items-center justify-center gap-2">
                        <CustomTooltipComponent
                            title={
                                canEditDocument
                                    ? "Modifica"
                                    : "La modifica e la notifica delle scadenze sono possibili solo per i dipendenti attivi"
                            }
                            placement="bottom"
                        >
                            <span className="inline-flex items-center">
                                <button
                                    disabled={!canEditDocument}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!canEditDocument) return;
                                        onEdit?.(doc);
                                    }}
                                    className={`inline-flex items-center justify-center text-xs font-semibold ${
                                        canEditDocument
                                            ? "text-gray-700 hover:text-indigo-700"
                                            : "text-gray-400 cursor-not-allowed"
                                    }`}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </button>
                            </span>
                        </CustomTooltipComponent>

                        <CustomTooltipComponent title="Apri anteprima">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewDoc({
                                        url: doc.download_link,
                                        name: doc.nome,
                                        description: doc.descrizione
                                    });
                                }}
                                className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                            >
                                <MagnifyingGlassIcon className="h-5 w-5" />
                            </button>
                        </CustomTooltipComponent>

                        <CustomTooltipComponent title="Elimina">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(doc.id);
                                }}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-red-600"
                            >
                                <TrashIcon className="h-5 w-5" /> 
                            </button>
                        </CustomTooltipComponent>
                    </div>
                </td>
            </tr>

            {/* === RIGA DETTAGLIO === */}
            {open && (
                <tr>
                    <td colSpan={6} className="bg-gray-50">
                        <div className="px-4 py-3 text-sm text-gray-700">

                            {/* ⬇︎  blocco metadati  ⬇︎ */}
                            <dl className="divide-y divide-gray-200">
                                {/* Descrizione */}
                                <div className="grid grid-cols-12 gap-x-2 py-2">
                                    <dt className="col-span-4 font-semibold text-gray-600">Descrizione</dt>
                                    <dd className="col-span-8">{doc.descrizione || "—"}</dd>
                                </div>

                                {/* Data caricamento */}
                                <div className="grid grid-cols-12 gap-x-2 py-2">
                                    <dt className="col-span-4 font-semibold text-gray-600">Data caricamento</dt>
                                    <dd className="col-span-8">
                                        {moment(doc?.created_at).format("DD/MM/YYYY HH:mm")}
                                    </dd>
                                </div>

                                {/* Data scadenza */}
                                <div className="grid grid-cols-12 gap-x-2 py-2">
                                    <dt className="col-span-4 font-semibold text-gray-600">Data scadenza</dt>
                                    <dd className="col-span-8">
                                        {doc.due_date ? moment(doc.due_date).format("DD/MM/YYYY") : "—"}
                                    </dd>
                                </div>

                                {canEditDocument && (
                                    <>
                                        {/* Promemoria scadenza */}
                                        {doc?.due_date && (
                                            <div className="grid grid-cols-12 gap-x-2 py-2">
                                                <dt className="col-span-4 font-semibold text-gray-600">Promemoria scadenza</dt>
                                                <dd className="col-span-8">
                                                {(effectiveTiming) + ' giorni' || "Nessun promemoria"}
                                                </dd>
                                            </div>
                                        )}

                                        {/* Email promemoria */}
                                        {doc?.due_date && effectiveTiming && (
                                        <div className="grid grid-cols-12 gap-x-2 py-2">
                                            <dt className="col-span-4 font-semibold text-gray-600">Email di notifica scadenza</dt>
                                            <dd className="col-span-8">
                                            {formatEmails(effectiveEmail) || "—"}
                                            </dd>
                                        </div>
                                        )}
                                    </>
                                )}
                                {/* Visualizza file */}
                                <div className="grid grid-cols-12 gap-x-2 py-2">
                                    <dt className="col-span-4 font-semibold text-gray-600">Visualizza file</dt>
                                    <dd className="col-span-8">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewDoc({
                                                url: doc.download_link,
                                                name: doc.nome,
                                                description: doc.descrizione
                                                });
                                            }}
                                            className="flex items-center gap-1 text-indigo-600 hover:underline"
                                        >
                                            <MagnifyingGlassIcon className="h-4 w-4" />
                                            <span className="text-sm font-medium">Apri anteprima</span>
                                        </button>
                                    </dd>
                                </div>

                                {/* Azioni */}
                                <div className="grid grid-cols-12 gap-x-2 py-2">
                                    <dt className="col-span-4 font-semibold text-gray-600 flex items-center">Azioni</dt>
                                    <dd className="col-span-8 flex gap-4">
                                        {/* toggle visibilità */}
                                        <CustomTooltipComponent title='Modifica visibilità' placement='bottom'>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onToggleVisible(doc.id, doc.visible_to_employee);
                                                }}
                                                className={`p-2 rounded ${
                                                    doc.visible_to_employee
                                                        ? 'hover:text-red-700'
                                                        : 'hover:text-green-700'
                                                } text-white`}
                                            >
                                                {doc.visible_to_employee ? (
                                                    <EyeIcon className="h-5 w-5 text-green-600" />
                                                ) : (
                                                    <EyeSlashIcon className="h-5 w-5 text-red-600" />
                                                )}
                                            </button>
                                        </CustomTooltipComponent>
                                    
                                        {/* modifica documento */}
                                        <CustomTooltipComponent
                                            title={
                                                canEditDocument
                                                    ? "Modifica"
                                                    : "La modifica e la notifica delle scadenze sono possibili solo per i dipendenti attivi"
                                            }
                                            placement="bottom"
                                        >
                                            <span className='inline-flex items-center'>
                                                <button
                                                    disabled={!canEditDocument}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!canEditDocument) return;
                                                        onEdit?.(doc);
                                                    }}
                                                    className={`inline-flex items-center gap-1 text-xs font-semibold ${
                                                        canEditDocument
                                                            ? "text-gray-700 hover:text-indigo-700"
                                                            : "text-gray-400 cursor-not-allowed"
                                                    }`}
                                                >
                                                    <EditOutlinedIcon className="h-5 w-5" fontSize="small" />
                                                </button>
                                            </span>
                                        </CustomTooltipComponent>

                                        {/* elimina */}
                                        <CustomTooltipComponent title='Elimina' placement='bottom'>
                                            <button
                                                onClick={() => onDelete(doc.id)}
                                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-red-600"
                                            >
                                                <TrashIcon className="h-5 w-5" /> 
                                            </button>
                                        </CustomTooltipComponent>
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}